import React, {useState, useMemo, useEffect} from 'react';
import {t} from 'i18next';
import {Box, Tabs, Tab, Typography} from '@mui/material';

import ArrButton from '../../../common/arr-button/ArrButton';
import './AvailableObjects.scss';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  ICommentList,
  IMetadataFieldList,
  ITagDataList,
} from '../../../../api/types/document.type';

interface TabProps {
  label?: string;
  disabled: boolean;
  isLoading: boolean;
  content: React.ReactNode;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
interface AvailableObjectsProps {
  commentList: ICommentList[];
  metadataFieldList: IMetadataFieldList[];
  tagDataList: ITagDataList[];
  isLoading: boolean;
  addSelectedToForm: (selectedItems: {
    tags: string[] | null;
    comments: string[] | null;
    metaDatas: string[] | null;
    specialObjects: string[] | null;
  }) => void;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-new-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
    name: `simple-tab-new-${index}`,
    'data-testid': `simple-tab-new-${index}`,
  };
}

const getAllDescendantIds = (node: any): string[] => {
  let ids: string[] = [];
  if (node.children && node.children.length > 0) {
    node.children.forEach((child: any) => {
      ids.push(child.id);
      const childDescendants = getAllDescendantIds(child);
      ids = [...ids, ...childDescendants];
    });
  }
  return ids;
};

const removeEmptyGroups = (nodes: any[]): any[] => {
  return nodes
    .map((node) => {
      if (node.hasChildren && node.children) {
        node.children = removeEmptyGroups(node.children);
        node.hasChildren = node.children.length > 0;
      }

      return node;
    })
    .filter((node) => {
      if (
        node.isGroup &&
        (!node.hasChildren || node.childrenIds.length === 0)
      ) {
        return false;
      }
      return true;
    });
};

const transformTags = (tags: ITagDataList[]) => {
  if (!tags || tags.length === 0) {
    return [];
  }

  const nodeMap = new Map<string, any>();

  tags.forEach((tag: ITagDataList) => {
    let nodeId = tag.id.toString();
    const parent = tag.parentID?.toString();

    if (nodeId === '-1') {
      nodeId = 'ALL_TAGS_ROOT';
    }

    nodeMap.set(nodeId, {
      id: nodeId,
      label: tag.name,
      name: tag.name,
      disabled: tag.disabled,
      parent: parent,
      originalId: tag.id,
      children: [],
      skipParent: tag.isGroup,
      isGroup: tag.isGroup,
    });
  });

  const roots: any[] = [];

  Array.from(nodeMap.values()).forEach((node: any) => {
    let parentId = node.parent;

    if (parentId === '-1') {
      parentId = 'ALL_TAGS_ROOT';
    }

    if (parentId === '0' || parentId === undefined || parentId === null) {
      roots.push(node);
    } else if (nodeMap.has(parentId)) {
      const parentNode = nodeMap.get(parentId);
      if (!parentNode.children) {
        parentNode.children = [];
      }
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const addMetadata = (node: any): void => {
    if (node.children && node.children.length > 0) {
      node.hasChildren = true;
      node.children.forEach((child: any) => addMetadata(child));
      node.childrenIds = getAllDescendantIds(node);
    } else {
      node.hasChildren = false;
      delete node.children;
      node.childrenIds = [];
    }
  };

  roots.forEach((root) => addMetadata(root));

  // Remove empty groups
  const cleanedRoots = removeEmptyGroups(roots);

  return cleanedRoots;
};

const transformComments = (comments: ICommentList[]) => {
  return comments.map((comment) => ({
    id: comment.commentId.toString(),
    label: comment.commentLabel,
    disabled: comment.disabled,
  }));
};
// Transform MetadataFieldList
const transformMetadataFields = (fields: IMetadataFieldList[]) => {
  return fields.map((field) => ({
    id: field.projectFieldID.toString(),
    label: field.projectFieldName,
    disabled: field.disabled,
  }));
};
// Special
export const specialObjects = () => {
  return [
    {
      id: '1',
      label: 'Static Text',
      type: 'statictext',
    },
    {
      id: '2',
      label: 'Radio Group',
      type: 'radiogroup',
    },
    {
      id: '3',
      label: 'Check Group',
      type: 'checkgroup',
    },
  ];
};

const AvailableObjects: React.FC<AvailableObjectsProps> = ({
  tagDataList,
  commentList,
  metadataFieldList,
  isLoading,
  addSelectedToForm,
}) => {
  const translate = (key: string) => t(`codingForms.availableObjects.${key}`);

  const tagLookup = useMemo(() => {
    const map: Record<string, ITagDataList> = {};
    tagDataList.forEach((tag) => {
      map[tag.id.toString()] = tag;
    });
    return map;
  }, [tagDataList]);

  const transformedTags = useMemo(
    () => transformTags(tagDataList),
    [tagDataList]
  );

  const allTagsRootId = useMemo(() => {
    const allTagsNode = transformedTags.find(
      (tag) => tag.id === 'ALL_TAGS_ROOT' || tag.originalId === -1
    );
    return allTagsNode?.id || undefined;
  }, [transformedTags]);

  const [expandedTagIds, setExpandedTagIds] = useState<string[]>(
    allTagsRootId ? [allTagsRootId] : []
  );

  useEffect(() => {
    if (allTagsRootId) {
      setExpandedTagIds((prev) =>
        prev.includes(allTagsRootId) ? prev : [allTagsRootId]
      );
    }
  }, [allTagsRootId]);

  const [currentTab, setcurrentTab] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, currentTab: number) => {
    setcurrentTab(currentTab);
  };
  // local state for Tags
  const [selectedTags, setSelectedTags] = useState<string[] | null>([]);
  const handleSelectedTags = (value: string[] | null): void => {
    setSelectedTags(value);
  };
  // local state for Comments
  const [selectedComments, setSelectedComments] = useState<string[] | null>([]);
  const handleSelectedComments = (value: string[] | null): void => {
    setSelectedComments(value);
  };
  // local state for Editable Metadata
  const [selectedEditableMetadata, setSelectedEditableMetadata] = useState<
    string[] | null
  >([]);
  const handleSelectedEditableMetadata = (value: string[] | null): void => {
    setSelectedEditableMetadata(value);
  };
  // local state for Special Objects
  const [selectedSpecialObjects, setSelectedSpecialObjects] = useState<
    string[] | null
  >([]);
  const handleSelectedSpecialObjects = (value: string[] | null): void => {
    setSelectedSpecialObjects(value);
  };

  const tabData: TabProps[] = [
    {
      label: 'Tags',
      disabled: false,
      isLoading: true,
      content: (
        <ArrTreeView
          itemsList={transformedTags}
          selectedItem={''}
          allowGlobalSelect={false}
          treeSelectedItems={selectedTags}
          defaultExpandedIds={expandedTagIds}
          setExpandedFolders={setExpandedTagIds}
          multipleSelection={true}
          isShowCountLabel={true}
          isShowCheckbox={true}
          showActions={true}
          customContentHeight={36}
          setSelectedItem={handleSelectedTags}
          checkboxGroupSelection={true}
          expandOnClickForNonCheckbox={true}
        />
      ),
    },
    {
      label: 'Comments',
      disabled: false,
      isLoading: true,
      content: (
        <ArrTreeView
          itemsList={transformComments(commentList)}
          selectedItem={''}
          allowGlobalSelect={false}
          treeSelectedItems={selectedComments}
          multipleSelection={true}
          isShowCountLabel={true}
          isShowCheckbox={true}
          showActions={true}
          customContentHeight={36}
          setSelectedItem={handleSelectedComments}
        />
      ),
    },
    {
      label: 'Editable Metadata',
      disabled: false,
      isLoading: true,
      content: (
        <ArrTreeView
          itemsList={transformMetadataFields(metadataFieldList)}
          selectedItem={''}
          allowGlobalSelect={false}
          treeSelectedItems={selectedEditableMetadata}
          multipleSelection={true}
          isShowCountLabel={true}
          isShowCheckbox={true}
          showActions={true}
          customContentHeight={36}
          setSelectedItem={handleSelectedEditableMetadata}
        />
      ),
    },
    {
      label: 'Special Objects',
      disabled: false,
      isLoading: true,
      content: (
        <ArrTreeView
          itemsList={specialObjects()}
          selectedItem={''}
          handleNoResultMessage={'hi'}
          allowGlobalSelect={false}
          treeSelectedItems={selectedSpecialObjects}
          multipleSelection={true}
          isShowCountLabel={true}
          isShowCheckbox={true}
          showActions={true}
          customContentHeight={36}
          setSelectedItem={handleSelectedSpecialObjects}
        />
      ),
    },
  ];
  function CustomTabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;
    return (
      <Box
        className="tab-content"
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && children}
      </Box>
    );
  }

  return (
    <Box className="available-objects-container">
      <Box className="top-part">
        <Typography
          className="typo-text subhead2_size"
          data-testid={`available-objects-avail-objects`}
        >
          {translate('availObjects')}
        </Typography>
        <ArrButton
          aria-label={translate('AddToForm')}
          variant="primary"
          size="small"
          disabled={
            selectedTags?.filter((id) => !tagLookup[id]?.isGroup)?.length ==
              0 &&
            selectedComments?.length == 0 &&
            selectedEditableMetadata?.length == 0 &&
            selectedSpecialObjects?.length == 0
          }
          onClick={() => {
            const tempObj = {
              tags: (selectedTags ?? []).filter(
                (id) => !tagLookup[id]?.isGroup
              ),
              comments: selectedComments,
              metaDatas: selectedEditableMetadata,
              specialObjects: selectedSpecialObjects,
            };
            addSelectedToForm(tempObj);
            setSelectedTags([]);
            setSelectedComments([]);
            setSelectedEditableMetadata([]);
            setSelectedSpecialObjects([]);
          }}
          dataTestId="available-objects-add-to-form-button"
        >
          {translate('AddToForm')}
        </ArrButton>
      </Box>
      <Box className="tab-headings">
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="basic tabs example"
          className="object-tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabData.length
            ? tabData.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  value={index}
                  {...a11yProps(index)}
                  className="object-tab"
                />
              ))
            : null}
        </Tabs>
      </Box>
      {isLoading ? (
        <ArrLoadingSpinner data-testid="arr-loading-spinner" />
      ) : tabData.length ? (
        tabData.map((tab, index) => (
          <CustomTabPanel key={tab.label} value={currentTab} index={index}>
            <Box className="title">
              <Typography
                className="typo-text subhead4_size"
                data-testid={`available-objects-group-${index}`}
              >
                Group: {tab.label?.toUpperCase()}
              </Typography>
            </Box>
            <Box className="content-tab">{tab.content}</Box>
          </CustomTabPanel>
        ))
      ) : null}
    </Box>
  );
};

export default AvailableObjects;
